import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api from "../lib/apiClient";
import { ChartCard, PieViz, LineViz } from "../components/charts";
import EmptyState from "../components/EmptyState";
import { Input } from "../components/ui/input";
import {
  MapPin, Truck, Circle, CheckCircle, WarningDiamond,
  ShoppingCart, Plus, Minus, ClipboardText, Gauge, ArrowsClockwise, Wrench,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const KPIS = [
  { key: "total_sites", label: "Mine Sites", icon: MapPin, accent: "bg-zinc-600" },
  { key: "total_vehicles", label: "Dumpers", icon: Truck, accent: "bg-zinc-600" },
  { key: "total_tyres", label: "Total Tyres", icon: Circle, accent: "bg-amber-500" },
  { key: "tyres_fitted", label: "Fitted", icon: CheckCircle, tone: "text-emerald-400", accent: "bg-emerald-500" },
  { key: "punctures_this_month", label: "Punctures / Month", icon: WarningDiamond, tone: "text-rose-400", accent: "bg-rose-500" },
];

const PERF = [
  { key: "total_km", label: "Total KM Delivered", suffix: " km" },
  { key: "avg_cpkm", label: "Avg CPKM", prefix: "₹", tone: "text-amber-400" },
  { key: "avg_tyre_life_hours", label: "Avg Tyre Life", suffix: " h" },
  { key: "total_inspections", label: "Inspections" },
  { key: "total_repairs", label: "Repairs", tone: "text-fuchsia-400" },
  { key: "tyres_removed", label: "Removed", tone: "text-zinc-300" },
  { key: "tyres_near_replacement", label: "Near Replacement", tone: "text-amber-400" },
  { key: "tyres_due_replacement", label: "Due Replacement", tone: "text-rose-400" },
  { key: "tyres_damaged", label: "Damaged / Attention", tone: "text-rose-400" },
  { key: "low_pressure_alerts", label: "Pressure Alerts", tone: "text-sky-400" },
  { key: "tyres_under_repair", label: "Under Repair", tone: "text-fuchsia-400" },
  { key: "tyres_in_stock", label: "Off-Vehicle / Stock" },
];

const EVENT_ICON = { purchase: ShoppingCart, fitment: Plus, removal: Minus, rotation: ArrowsClockwise, puncture: WarningDiamond, inspection: ClipboardText, pressure: Gauge, repair: Wrench, retread: ArrowsClockwise };
const EVENT_COLOR = { purchase: "text-sky-400", fitment: "text-emerald-400", removal: "text-zinc-400", rotation: "text-teal-400", puncture: "text-rose-400", inspection: "text-amber-400", pressure: "text-violet-400", repair: "text-fuchsia-400", retread: "text-fuchsia-400" };

function useCountUp(target, dur = 650) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof target !== "number") { setN(0); return; }
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return n;
}

const KPI_INITIAL = { opacity: 0, y: 12 };
const KPI_ANIMATE = { opacity: 1, y: 0 };
const KPI_TRANSITION = { duration: 0.25, ease: "easeOut" };
const KPI_HOVER = { y: -2 };

function KpiCard({ k, value }) {
  const n = useCountUp(typeof value === "number" ? value : 0);
  return (
    <motion.div initial={KPI_INITIAL} animate={KPI_ANIMATE} transition={KPI_TRANSITION}
      whileHover={KPI_HOVER} data-testid={`kpi-${k.key}`}
      className="relative overflow-hidden rounded-sm border border-zinc-800 bg-zinc-900 p-4 ring-1 ring-white/5 transition-colors hover:border-zinc-700">
      <span className={`absolute left-0 top-0 h-full w-1 ${k.accent}`} />
      <div className="flex items-center justify-between">
        <span className="overline text-zinc-500">{k.label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-sm bg-zinc-800 ${k.tone || "text-zinc-400"}`}>
          <k.icon size={16} weight="bold" />
        </span>
      </div>
      <div className={`mt-3 font-display text-3xl font-extrabold tabular-nums tracking-tight ${k.tone || "text-zinc-50"}`}>
        {value == null ? "—" : n.toLocaleString()}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("all");
  const [activityDate, setActivityDate] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { api.get("/sites").then(({ data }) => setSites(data)); }, []);
  useEffect(() => {
    const qs = siteId !== "all" ? `?mine_site_id=${siteId}` : "";
    api.get(`/dashboard/stats${qs}`).then(({ data }) => setStats(data));
    api.get(`/dashboard/charts${qs}`).then(({ data }) => setCharts(data));
  }, [siteId]);
  useEffect(() => {
    const p = new URLSearchParams();
    if (siteId !== "all") p.set("mine_site_id", siteId);
    if (activityDate) p.set("date", activityDate);
    const qs = p.toString() ? `?${p.toString()}` : "";
    api.get(`/dashboard/overview${qs}`).then(({ data }) => setOverview(data));
  }, [siteId, activityDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-500">Fleet tyre intelligence at a glance</p>
        </div>
        <Select value={siteId} onValueChange={setSiteId}>
          <SelectTrigger data-testid="filter-site" className="w-52 bg-zinc-900"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Mine Sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {KPIS.map((k) => <KpiCard key={k.key} k={k} value={stats ? stats[k.key] : null} />)}
      </div>

      {/* Performance metrics */}
      <div>
        <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold tracking-tight text-zinc-300"><Gauge size={16} className="text-amber-400" /> Tyre Performance & Lifecycle</h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          {PERF.map((p) => (
            <div key={p.key} data-testid={`perf-${p.key}`} className="rounded-sm border border-zinc-800 bg-zinc-900 p-4 ring-1 ring-white/5">
              <div className="overline text-zinc-500">{p.label}</div>
              <div className={`mt-2 font-display text-2xl font-extrabold tabular-nums tracking-tight ${p.tone || "text-zinc-50"}`}>
                {stats == null ? "—" : `${p.prefix || ""}${(stats[p.key] || 0).toLocaleString()}${p.suffix || ""}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      {charts && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7"><ChartCard title="Punctures by Day" testid="chart-punctures"><LineViz data={charts.punctures_by_day} /></ChartCard></div>
          <div className="lg:col-span-5"><ChartCard title="Tyre Failure Reasons" testid="chart-failures"><PieViz data={charts.failure_reasons} /></ChartCard></div>
        </div>
      )}

      {/* Recent activity */}
      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5" data-testid="recent-activity">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-zinc-200"><ClipboardText size={16} className="text-sky-400" /> {activityDate ? `Activity on ${new Date(activityDate).toLocaleDateString()}` : "Recent Activity"}</h3>
          <div className="flex items-center gap-2">
            <Input type="date" data-testid="activity-date" value={activityDate} max={today} onChange={(e) => setActivityDate(e.target.value)} className="h-8 w-36 bg-zinc-950 text-xs" />
            {activityDate && <button onClick={() => setActivityDate("")} data-testid="activity-date-clear" className="text-xs text-zinc-500 transition-colors hover:text-amber-400">Latest</button>}
          </div>
        </div>
        {(overview?.recent_activity || []).length === 0 ? (
          <EmptyState icon={ClipboardText} title={activityDate ? "No activity on this date" : "No activity yet"} hint={activityDate ? "Try another date." : "Fitments, removals and inspections will show up here."} testid="no-activity" />
        ) : (
          <ul className="space-y-3">
            {overview.recent_activity.map((e) => {
              const Icon = EVENT_ICON[e.type] || Circle;
              return (
                <li key={e.id} className="flex items-start gap-3" data-testid={`activity-${e.id}`}>
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-zinc-800 ${EVENT_COLOR[e.type] || "text-zinc-400"}`}>
                    <Icon size={14} weight="bold" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-zinc-200">{e.serial_number && <span className="font-mono font-semibold text-amber-500">{e.serial_number} </span>}{e.description}</p>
                    <p className="text-xs text-zinc-500">{e.timestamp ? new Date(e.timestamp).toLocaleDateString() : ""}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
